import cn from 'classnames';
import PropTypes from 'prop-types';

import Address from 'components/checkout/caap/address/Address';
import ContentBox from 'components/checkout/caap/ContentBox';
import ListItem from 'components/checkout/caap/ListItem';
import ModalOverlay from 'components/checkout/caap/ModalOverlay';
import { Row, RowSpan } from 'components/checkout/caap/Layout';
import { pluralize } from 'helpers';
import css from 'styles/components/checkout/caap/address/suggestedAddresses.scss';

const SuggestedAddresses = (componentParams, { testId }) => {
  const {
    address,
    isBilling = false,
    isFetchingCheckoutData,
    onCloseModalOverlayClick,
    onSuggestedAddressSelected,
    onUseThisAddressClick,
    selectedAddressId,
    showAsModal = true,
    suggestedAddresses
  } = componentParams;
  const addressType = isBilling ? 'billing' : 'shipping';
  const useThisAddressText = isBilling ? 'Use This Billing Address' : 'Ship to this address';
  const suggestedAddressTitle = `Suggested ${pluralize('Address', suggestedAddresses.length)}:`;
  const containerStyle = cn({ [css.inlineFormContainer]: !showAsModal });

  return (
    <div className={containerStyle}>
      <ModalOverlay onCloseModalOverlayClick={onCloseModalOverlayClick} showAsModal={showAsModal}>

        <form method="post" onSubmit={onUseThisAddressClick}>
          {
            showAsModal
              ? <div className={css.modalTitleContainer}>
                Verify your {addressType} address
                <button
                  type="button"
                  className={css.closeButton}
                  data-test-id={testId('closeModal')}
                  onClick={onCloseModalOverlayClick}
                  onKeyPress={onCloseModalOverlayClick}
                  role="link"
                  tabIndex="0"
                  title="Close Modal"></button>
              </div>
              : null
          }

          <div className={css.contentWrapper}>
            <div className={css.suggestedAddressDescr}>
              There's a problem with the address provided - we've marked our suggestions <span className={css.suggestedAddressHighlight}> in red</span> below. Please choose which version of the address you want to use.
            </div>

            <div className={css.addresses}>
              <h4>Original Address:</h4>
              <ContentBox isSolo={true} isSelected={selectedAddressId === address.addressId}>
                <ListItem
                  itemKey={address.addressId}
                  index={address.addressId}
                  onChange={() => onSuggestedAddressSelected(address.addressId)}
                  isChecked={selectedAddressId === address.addressId}>
                  <Row extraClass={css.addressBlock}>
                    <RowSpan span="12">
                      <Address address={address} isBaseForHighlight={true} multiLineDisplay={true} />
                    </RowSpan>
                  </Row>
                </ListItem>
              </ContentBox>

              <h4>{suggestedAddressTitle}</h4>

              {
                suggestedAddresses.map((suggestedAddress, index) => {
                  const {
                    mailingAddress: {
                      addressLine1,
                      addressLine2 = '',
                      city,
                      stateOrRegion,
                      postalCode
                    }
                  } = suggestedAddress;

                  const suggestedAddressId = `${address.addressId}-${index}`;

                  const savedAddress = { fullName: address.fullName, addressLine1, addressLine2, city, stateOrRegion, postalCode };

                  return (
                    <ContentBox
                      isLast={suggestedAddresses.length > 1 && index === suggestedAddresses.length}
                      isFirst={suggestedAddresses.length > 1 && index === 0}
                      isSolo={suggestedAddresses.length === 1}
                      isSelected={selectedAddressId === suggestedAddressId}
                      key={suggestedAddressId}>
                      <ListItem
                        itemKey={suggestedAddressId}
                        index={suggestedAddressId}
                        onChange={() => onSuggestedAddressSelected(suggestedAddressId)}
                        isChecked={selectedAddressId === suggestedAddressId}>
                        <Row extraClass={css.addressBlock}>
                          <RowSpan span="12">
                            <Address address={savedAddress} baseAddress={address} multiLineDisplay={true} />
                          </RowSpan>
                        </Row>
                      </ListItem>
                    </ContentBox>
                  );
                })
              }
            </div>
          </div>
          <div className={css.continueButtonContainer}>
            <button
              type="submit"
              className={css.checkoutPrimaryButton}
              data-test-id={testId('shipToSuggestedAddress')}
              disabled={isFetchingCheckoutData}
              onClick={onUseThisAddressClick}>
              {useThisAddressText}
            </button>
          </div>
        </form>
      </ModalOverlay>
    </div>
  );
};

SuggestedAddresses.contextTypes = {
  testId: PropTypes.func
};

export default SuggestedAddresses;
